import Customer from "./customer";
import SaleItem from "./saleItem";
import EconActivity from "./econActivity";
import EDocument from "./edocument";
import store from '../store'
import Big from "big.js";
import { computeTaxTotals, baseAtRate } from "./taxCatalog";

// Cota del campo de observaciones. Espeja `Sale.observations` del backend
// (backend/models.py): de ese max_length dependen el XML que se envia al SRI
// y el RIDE, que recortan el texto si se pasa.
export const OBSERVATIONS_MAX_LENGTH = 400;

const Sale = class {
    constructor(object) {
        if (object === undefined) {
            this.id = null
            this.proformaId = null
            this.serialNumber = null
            this.econActivity = null;
            this.customer = new Customer();
            this.table = null; // Mesa de la cuenta (restaurante): {id, name}
            this.items = [];
            this.createdAt = new Date();
            this.closedAt = null;
            this.creditDays = 0;
            this.total = new Big('0.0');
            this.discount = new Big('0.0');
            this.sub0 = new Big('0.0');
            this.sub12 = new Big('0.0');
            this.tax = new Big('0.0');
            // Desglose por tarifa (fuente de sub5 / subGeneral en la caja de totales).
            this.taxBuckets = [];
            this.iceValue = new Big('0.0');
            this.tip = new Big('0.0');
            this.taxPercentage = store.getters.business.taxPercentage;
            this.edoc = null;
            this.editable = true;
            this.paymentMethod = {name: 'Efectivo', code: 'CASH'};
            this.collectStatus = null; // To reference if is paid or not
            this.edocStatus = null; // To know the edoc status if emitted
            this.annulmentStatus = null; // Estado de la anulación en la fila de la lista (H54)
            this.observations = null;
            this.paidAmount = new Big('0.0'); // Σ abonos registrados
            this.pendingAmount = null; // Saldo por cobrar (null = sin dato del backend)
            this.payments = []; // Abonos registrados (serializados)
            // Notas de crédito del cliente elegidas para pagar la venta al
            // cerrarla. Solo viven en el formulario: el backend las canjea al
            // guardar (saveSale) y desde ahí aparecen como abonos en `payments`.
            this.creditNotesToRedeem = [];
            // Consecuencias de anular (liquidacion de tarjeta ya cobrada, ICE
            // cobrado). Lo llena el backend solo en getSale; el resto de
            // respuestas lo omite y la venta queda sin restricciones conocidas.
            this.annulment = {blockers: [], warnings: []};
        } else {
            this.id = object.id;
            this.proformaId = null;
            this.serialNumber = object.serialNumber;
            this.econActivity = new EconActivity(object.activity);
            this.customer = new Customer(object.customer);
            this.table = object.table || null;
            this.createdAt = new Date(Date.parse(object.createdAt));
            this.closedAt = new Date(Date.parse(object.closedAt));
            this.creditDays = object.creditDays;
            this.total = (object.total !== undefined && object.total !== null) ? new Big(object.total) : new Big('0.0');
            this.discount = (object.discount !== undefined && object.discount !== null) ? new Big(object.discount) : new Big('0.0');
            this.sub0 = (object.base0 !== undefined && object.base0 !== null) ? new Big(object.base0) : new Big('0.0');
            this.sub12 = (object.base12 !== undefined && object.base12 !== null) ? new Big(object.base12) : new Big('0.0');
            this.tax = (object.tax !== undefined && object.tax !== null) ? new Big(object.tax) : new Big('0.0');
            this.taxBuckets = Array.isArray(object.taxLines) ? object.taxLines : [];
            this.iceValue = (object.iceValue !== undefined && object.iceValue !== null) ? new Big(object.iceValue) : new Big('0.0');
            this.tip = (object.tip !== undefined && object.tip !== null) ? new Big(object.tip) : new Big('0.0');
            this.taxPercentage = object.taxPercentage || store.getters.business.taxPercentage;
            this.editable = object.editable;
            this.paymentMethod = object.paymentMethod;
            this.collectStatus = object.status; // To reference if is paid or not
            this.edocStatus = object.edocStatus; // To know the edoc status if emitted
            this.annulmentStatus = object.annulmentStatus || null; // H54
            this.observations = object.observations;
            this.paidAmount = (object.paidAmount !== undefined && object.paidAmount !== null) ? new Big(object.paidAmount) : new Big('0.0');
            this.pendingAmount = (object.pendingAmount !== undefined && object.pendingAmount !== null) ? new Big(object.pendingAmount) : null;
            this.payments = (object.payments !== undefined && object.payments !== null) ? object.payments : [];
            this.creditNotesToRedeem = [];
            this.annulment = {
                blockers: (object.annulment && Array.isArray(object.annulment.blockers)) ? object.annulment.blockers : [],
                warnings: (object.annulment && Array.isArray(object.annulment.warnings)) ? object.annulment.warnings : [],
            };

            // Parse items if exists
            if (object.items !== undefined && object.items !== null && object.items.length > 0) { 
                this.items = object.items.map(item => new SaleItem(item)); 
            }

            // Parse edoc if exists
            if (object.edoc !== undefined && object.edoc !== null) {
                this.edoc = new EDocument(object.edoc);
            } else { this.edoc = null }
        }
    }
    // La cuenta de una mesa se muestra con su mesa delante («Mesa 1 - Paul»): con dos Paul en el
    // local el nombre solo no alcanza. Solo para mostrar; el nombre del cliente no cambia.
    get displayName() {
        return this.table ? `${this.table.name} - ${this.customer.name}` : this.customer.name;
    }
    get createdAtFormatted() {
        var options = { year: 'numeric', month: 'numeric', day: 'numeric'};
        return this.createdAt.toLocaleDateString('es-EC', options) + " - " + this.createdAt.toLocaleTimeString('en-US');
    }
    get dueDate() {
        if (this.creditDays > 0) {
            var dueDate = (this.createdAt !== null) ? new Date(this.createdAt) : new Date();
            dueDate.setDate(dueDate.getDate() + parseInt(this.creditDays));
            return dueDate;
        }
        return null;
    }
    get dueDateFormatted() {
        if (this.dueDate !== null) {
            var options = { year: 'numeric', month: 'numeric', day: 'numeric'};
            return this.dueDate.toLocaleDateString('es-EC', options);
        }
        return null;
    }
    get isPaid() {
        return (this.collectStatusReadable === 'COBRADA');
    }
    // Σ de las notas de crédito elegidas para pagar la venta (número). Lo usa
    // AccountEntry para cobrar solo lo que falta.
    get creditNoteAmount() {
        return (this.creditNotesToRedeem || []).reduce((sum, note) => sum + Number(note.total.toString()), 0);
    }
    get annulmentBlockers() {
        return (this.annulment && Array.isArray(this.annulment.blockers)) ? this.annulment.blockers : [];
    }
    get annulmentWarnings() {
        return (this.annulment && Array.isArray(this.annulment.warnings)) ? this.annulment.warnings : [];
    }
    // Falso solo cuando el backend reporto un bloqueo (venta ya liquidada por la
    // pasarela). El backend revalida en /sale/delete: esto es UX, no seguridad.
    get canBeAnnulled() {
        // Ya hay una solicitud en curso ante el SRI: pedir otra la duplicaria.
        if (this.edoc !== null && this.edoc !== undefined && this.edoc.annulmentInFlight) {
            return false;
        }
        return this.annulmentBlockers.length === 0;
    }
    // A una factura a consumidor final el SRI no le admite nota de credito: ni el
    // aviso de "No se puede anular" la ofrece, ni el formulario de nota de credito
    // deja emitirla. Sale del bloqueo que manda el backend en `annulment`, que
    // viene cuando la venta se pide con annulment=True (dashboard/sale/get).
    get allowsCreditNote() {
        return !this.annulmentBlockers.some(blocker => blocker.code === 'finalConsumerInvoice');
    }
    // Facturar una venta ARCHIVADA a consumidor final (o a la etiqueta de una mesa, con nueves)
    // pregunta antes a nombre de quién va la factura: la tarjeta del cliente ya no se edita y,
    // desde $50, el SRI pide los datos del cliente (backend/services/fiscal.py).
    get asksInvoiceBuyer() {
        return this.id !== null && this.editable === false && this.edoc === null
            && !!this.customer && this.customer.isConsumidorFinal;
    }

    // Mensaje del estado de la anulacion, o null si no hay ninguna en curso.
    get annulmentStateMessage() {
        if (this.edoc === null || this.edoc === undefined) { return null }
        return this.edoc.annulmentMessage;
    }
    get state() {
        if (this.editable === true) { return "GUARDADA" }
        else if (this.edoc === null) { return "ARCHIVADA" }
        // Anulada aca pero vigente para el SRI: se distingue porque queda algo
        // pendiente de hacer (anularla en el portal o emitir nota de credito).
        else if (this.edoc.nulled === true && this.edoc.annulmentUnreported === true) { return "ANULADA (SIN REPORTAR)" }
        else if (this.edoc.nulled === true) { return "ANULADA" }
        // Estados propios: el comprobante SIGUE VALIDO (por eso van antes de
        // FACTURADA pero nunca se muestran como ANULADA) y sigue contando en
        // reportes y cuentas por cobrar. Ocultarlo haria creer al usuario que el
        // sistema no registro su anulacion.
        else if (this.edoc.annulmentSending === true) { return "ANULANDO" }
        else if (this.edoc.annulmentPending === true) { return "ANULACIÓN SOLICITADA" }
        else if (this.edoc.authorized === true) { return "FACTURADA" }
        else { return "POR REPORTAR" }
    }
    get stateBadgeColor() {
        if (this.editable === true) { return "blue" }
        else if (this.edoc === null) { return "gray" }
        else if (this.edoc.nulled === true) { return "red" }
        else if (this.edoc.authorized === true) { return "green" }
        else { return "yellow" }
    }
    // Rótulos de la lista, los mismos del formulario (H54): antes una anulación sin reportar
    // salía «ANULADA» y una solicitud en curso, «POR REPORTAR».
    get edocState() {
        if (this.editable === true) { return "GUARDADA" }
        else if (this.edocStatus === "none") { return "ARCHIVADA" }
        else if (this.edocStatus === "nulled") {
            return this.annulmentStatus === "unreported" ? "ANULADA (SIN REPORTAR)" : "ANULADA";
        }
        else if (this.edocStatus === "annulmentSending") { return "ANULANDO" }
        else if (this.edocStatus === "annulmentPending") { return "ANULACIÓN SOLICITADA" }
        else if (this.edocStatus === "authorized") { return "FACTURADA" }
        else if (this.edocStatus === "received") { return "POR AUTORIZAR" }
        else { return "POR REPORTAR" }
    }
    get edocStateBadgeColor() {
        if (this.editable === true) { return "blue" }
        else if (this.edocStatus === "none") { return "gray" }
        else if (this.edocStatus === "nulled") { return "red" }
        else if (this.edocStatus === "authorized") { return "green" }
        else if (this.edocStatus === "received") { return "yellow" }
        else if (this.edocStatus === "annulmentSending" || this.edocStatus === "annulmentPending") { return "yellow" }
        else { return "orange" }
    }
    get hasPartialPayments() {
        // Venta por cobrar con al menos un abono registrado
        return this.collectStatus === "PENDING" && this.paidAmount !== undefined
            && this.paidAmount !== null && Number(this.paidAmount.toString()) > 0;
    }
    get collectStatusReadable() {
        if (this.edocStatus === "nulled") { return "NO APLICA" }
        if (this.collectStatus === "PAID") { return "COBRADA"}
        if (this.collectStatus === "PENDING" && !this.editable && this.dueDate !== null && this.dueDate <= new Date()) { return "VENCIDA"}
        if (this.hasPartialPayments && !this.editable) { return "ABONADA" }
        else { return "POR COBRAR" }
    }
    get collectStatusBadgeColor() {
        if (this.edocStatus === "nulled") { return "red" }
        if (this.collectStatus === "PAID") { return "green"}
        if (this.collectStatus === "PENDING" && !this.editable && this.dueDate !== null && this.dueDate <= new Date()) { return "orange"}
        if (this.hasPartialPayments && !this.editable) { return "yellow" }
        else { return "blue" }
    }
    get paymentMethodReadable() {
        if (this.paymentMethod === "CASH") { return "Efectivo"}
        if (this.paymentMethod === "TRANSFER") { return "Transferencia"}
        if (this.paymentMethod === "DEBIT_CARD") { return "Tarj. Debito"}
        if (this.paymentMethod === "CREDIT_CARD") { return "Tarj. Credito"}
        if (this.paymentMethod === "MIXED") { return "Mixto"}
        if (this.paymentMethod === "CREDIT_NOTE") { return "Nota de crédito"}
        else { return "Cheque" }
    }
    get paymentMethodBadgeColor() {
        if (this.paymentMethod === "CASH") { return "green"}
        if (this.paymentMethod === "TRANSFER") { return "blue"}
        else { return "gray" }
    }
    updateItems(item, append=true) {
        if (append) { this.items.unshift(item) }
        else {
            let index = this.getItemIndex(item);
            if (index === -1) { this.items.unshift(item) } 
            else { this.items[index] = item }
        }
        this.updateValues();
     }
    deleteItem(item) {
        let index = this.getItemIndex(item);
        this.items.splice(index, 1);
        if (this.items.length == 0) { this.items = [] }
        this.updateValues();
    }
    getItemIndex(item) {
        var index = -1;
        if (item.uid === null || this.items === null || this.items === undefined) { return -1; }
        for (var i=0; i<this.items.length; i++) {
            if (this.items[i].uid === item.uid) { return i; }
        }
        return index;
    }

    // Desglose por tarifa para la caja de totales. Se muestra SIEMPRE una fila
    // por tarifa (0% / 5% / general) aunque esté en cero: es el formato estándar
    // del SRI y el mismo que emite el RIDE. Agrupar 5% y 15% en un solo
    // "subtotal gravado" oculta información que el comprobante sí detalla.
    get sub5() {
        return baseAtRate(this.taxBuckets, '5');
    }

    // Todo lo gravado que NO es 5%. Se calcula por resta para que ninguna base
    // desaparezca de la vista (p.ej. un documento histórico al 12% o al 14%).
    // Etiqueta de la fila de tarifa general. Usa la tarifa del DOCUMENTO (que en
    // un comprobante histórico puede ser 12% o 14%, no la vigente) y cae a la del
    // negocio si viniera vacía, para no mostrar nunca un "Subtotal %" sin número.
    get subGeneralLabel() {
        const rate = this.taxPercentage || store.getters.business.taxPercentage || '15';
        return 'Subtotal ' + rate + '%';
    }

    get subGeneral() {
        return this.sub12.minus(this.sub5);
    }

    updateValues() {
        var discount_ = new Big('0.0');
        var taxLines = [];
        for (var i=0; i<this.items.length; i++) {
            var base = this.items[i].amount.times(this.items[i].unitPrice).times(this.items[i].discount.times(-1).plus(1));
            // ICE: si el item trae su producto, se recalcula para que no quede
            // desactualizado al cambiar la cantidad; si no (item ya persistido),
            // se usa el monto congelado que devolvió el backend.
            var prod = this.items[i].productObj;
            var itemIce = (prod && prod.hasIce && prod.estimateIce)
                ? prod.estimateIce(this.items[i].amount, base)
                : this.items[i].iceValue;
            taxLines.push({ ivaCode: this.items[i].ivaCode, base: base,
                            iceValue: itemIce, iceCode: this.items[i].iceCode });
            discount_ = discount_.plus(this.items[i].amount.times(this.items[i].unitPrice).times(this.items[i].discount));
        }
        // El IVA se agrega POR TARIFA (un documento puede mezclar 5% y 15%), no
        // aplicando una sola tarifa del negocio a toda la base gravada.
        // computeTaxTotals es espejo del backend: redondea cada tarifa a 2
        // decimales y de ahí deriva sub0/sub12/tax, así el invariante
        // sub0 + sub12 + tax == total se cumple y coincide al centavo con la BD.
        var totals = computeTaxTotals(taxLines);
        this.taxBuckets = totals.buckets;
        this.sub0 = totals.sub0;
        this.sub12 = totals.sub12;
        this.tax = totals.tax;
        // El ICE suma al total y NO entra en sub0/sub12 (esas son las bases limpias).
        this.iceValue = totals.iceTotal;
        this.discount = discount_.round(2);
        this.total = this.sub0.plus(this.sub12).plus(this.iceValue).plus(this.tax).round(2);
    }
    loadFromProforma(proformaId) {
        this.id = null;
        this.editable = true;
        this.creditDays = 0;
        this.proformaId = proformaId;
    }
}

export default Sale